import axios, { InternalAxiosRequestConfig } from "axios";
import EventEmitter from "events";
import env from "../../utils/env.config";
import { getCookie, removeCookie } from "../CookieHelper";
import { COOKIE_ENUM } from "../enums";
import { CREATE_VISITOR_PATH, ensureVisitorToken } from "./visitorToken";

axios.defaults.baseURL = env.baseUrl;

export const interceptorEmitter = new EventEmitter();

axios.interceptors.request.use(
  function (config) {
    // Do something before request is sent
    if (typeof window !== "undefined") {
      if (config && config.headers) {
        config.headers.version = env.apiV2;
        const token: string = getCookie(COOKIE_ENUM.TOKEN) as string;
        if (!config.headers.Authorization && token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      // if franchise id not found emit an event to call link table to user, app context is listening to this event
      if (config && config.data && config.data.franchiseId === -1) {
        interceptorEmitter.emit("linkTableToUser");
      }
    }

    return config;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

const unauthorisedPages = ["/404"];

axios.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    if (response && response.status !== 200) {
      // router.push("/404");
      // location('/404')
    }

    return response;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    const config: RetryableRequestConfig | undefined = error.config;
    if (
      error.response?.status !== 401 ||
      unauthorisedPages.includes(window.location.pathname) ||
      error.response?.config?.baseURL !== axios.defaults.baseURL ||
      !config ||
      config.url?.includes(CREATE_VISITOR_PATH)
    ) {
      return Promise.reject(error);
    }

    const sentToken = getBearerToken(config);
    const currentToken = getCookie(COOKIE_ENUM.TOKEN);
    // Only drop the token this request was refused with. Requests that left
    // together 401 together; once one of them has stored a fresh token, the
    // rest must not delete it.
    if (sentToken && currentToken === sentToken) {
      removeCookie(COOKIE_ENUM.TOKEN);
    }
    const nextToken =
      currentToken && currentToken !== sentToken
        ? Promise.resolve(currentToken)
        : ensureVisitorToken();

    return nextToken.then(
      (token) => {
        // A request sent without any token was refused before it ran as
        // anybody, so it is safe to send again now that there is one. A request
        // whose token was rejected is not replayed: the session it belonged to
        // is gone, and the caller has to decide what that means (e.g. checkout
        // sends the customer to log in).
        if (!sentToken && !config.retriedWithToken) {
          config.retriedWithToken = true;
          config.headers.Authorization = `Bearer ${token}`;

          return axios(config);
        }

        return Promise.reject(error);
      },
      () => Promise.reject(error)
    );
  }
);

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  retriedWithToken?: boolean;
};

const getBearerToken = (config: InternalAxiosRequestConfig) => {
  const header = config.headers?.Authorization;

  return typeof header === "string" && header.startsWith("Bearer ")
    ? header.slice("Bearer ".length)
    : undefined;
};
